import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { editEventSharedWith, getEventSharedWith } from "../../../redux/actions/events";
import { RevAlert, RevButton, RevCard, RevChip, RevEmpty, RevField, RevKvRow, SHARE_STATUS_TONE } from "../../../components/ds";
import { formatShortDate } from "../../../helpers/functions";

const STATUS_LINE_KEY = {
    pending: 'eventSharedWith.status.pending',
    accepted: 'eventSharedWith.status.accepted',
    removed: 'eventSharedWith.status.removed',
    denied: 'eventSharedWith.status.denied',
};

const EventSharedWith = ({ event, getEventSharedWith, editEventSharedWith, user, loading }) => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const [sharedEmails, setSharedEmails] = useState(event.sharedWith || []);
    const [originalEmails, setOriginalEmails] = useState(event.sharedWith || []);
    const [mailError, setMailError] = useState('');
    const [newEmail, setNewEmail] = useState('');

    // Un array vacío SÍ es una lista: «no comparto con nadie» es un estado legítimo y deja
    // invitar. Lo que no es lista es `undefined`, que significa «todavía no ha llegado» o
    // «el GET falló» — y ahí cualquier `edit` mandaría una lista incompleta y borraría del
    // servidor lo que hubiera. La acción se come el error y baja `loading` en su `finally`,
    // así que sin lista y sin carga en vuelo el GET ha fallado: la guarda de primera carga
    // se resuelve también por el camino de error, no solo por el de éxito.
    const listaLista = Array.isArray(event.sharedWith);
    const cargaFallida = !listaLista && !loading;

    useEffect(() => {
        if (!event.sharedWith) {
            getEventSharedWith(id);
        }
    }, [id, event.sharedWith, getEventSharedWith]);

    // La pestaña ya no se desmonta mientras carga, así que la lista del servidor llega con
    // el componente vivo y hay que resincronizar: sin esto, los dos estados se quedan en el
    // `[]` del montaje y el primer «Invitar» manda solo el correo nuevo, borrando del
    // servidor a todos los demás (y «Dejar de compartir» no encuentra nada que cambiar).
    // Se ponen los DOS a la vez para que `arraysEqual` no dispare un `edit` espurio, y esto
    // reajusta también la referencia después de cada guardado.
    useEffect(() => {
        if (event.sharedWith) {
            setSharedEmails(event.sharedWith);
            setOriginalEmails(event.sharedWith);
        }
    }, [event.sharedWith]);

    useEffect(() => {
        if (!arraysEqual(originalEmails, sharedEmails)) {
            editEventSharedWith(id, sharedEmails);
        }
    }, [sharedEmails, originalEmails, id, editEventSharedWith]);

    const arraysEqual = (a, b) => {
        return a.length === b.length && a.every((val, index) => val === b[index]);
    };

    const addEmail = () => {
        if (!newEmail.trim()) {
            setMailError(t("Email is required"));
            return;
        }

        const emailList = newEmail.split(/[ ,]+/).filter(Boolean);
        const emailsToAdd = emailList
            .map(email => email.toLowerCase())
            .filter(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !sharedEmails.some(item => item.email.toLowerCase() === email));

        if (!emailsToAdd.length) {
            setMailError(t("No new emails were added"));
            return;
        }

        // check if some of the emails are already de own mail (user.email)
        if (emailsToAdd.includes(user.email)) {
            setMailError(t("You can't share the event with yourself"));
            return;
        }

        const newSharedEmails = [
            ...sharedEmails,
            ...emailsToAdd.map(email => ({ email, status: "pending", permission: "read", notify: true }))
        ];
        setSharedEmails(newSharedEmails);
        setNewEmail("");
    };

    const handleChangeMail = (e) => {
        setMailError('');
        setNewEmail(e.target.value);
    };

    const handleStatusChange = (email, newStatus) => {
        const updatedSharedEmails = sharedEmails.map(item => item.email === email ? { ...item, status: newStatus, notify: (newStatus === "pending" ? true : false) } : item);
        setSharedEmails(updatedSharedEmails);
    };

    return (
        <>
            <div className="rev-cols rev-cols--flush">
                <div className="rev-cols__main">
                    <RevCard title={t('eventSharedWith.invite.title')}>
                        <span className="rev-help">{t('eventSharedWith.invite.help')}</span>
                        <RevField label={t('eventSharedWith.invite.label')} htmlFor="email">
                            <div className="rev-field-row rev-field-row--gap">
                                <input id="email" className="rev-input rev-grow" type="email" value={newEmail} placeholder={t('eventSharedWith.invite.placeholder')} onChange={handleChangeMail} />
                                <RevButton variant="primary" onClick={addEmail} disabled={!listaLista || !newEmail.trim() || Boolean(mailError)}>{t('Invite')}</RevButton>
                            </div>
                            {mailError && <span role="alert" className="rev-help rev-help--err">{mailError}</span>}
                        </RevField>
                    </RevCard>

                    <RevCard title={t('Shared With')}>
                        {cargaFallida &&
                            <RevAlert tone="err" title={t('eventSharedWith.loadError.title')}
                                actions={<RevButton size="sm" onClick={() => getEventSharedWith(id)}>{t('eventSharedWith.loadError.retry')}</RevButton>}>
                                <span>{t('eventSharedWith.loadError.body')}</span>
                            </RevAlert>
                        }
                        {!loading && event?.sharedWith?.length === 0 &&
                            <RevEmpty icon="person_add" title={t('eventSharedWith.empty.title')} body={t('eventSharedWith.empty.body')} />
                        }
                        {event?.sharedWith?.map((item, index) => {
                            const sharedDate = item.sharedDate ? new Date(item.sharedDate) : null;
                            const hasValidDate = sharedDate && !isNaN(sharedDate.getTime());
                            const statusLine = STATUS_LINE_KEY[item.status] ? t(STATUS_LINE_KEY[item.status]) : '';
                            const dateText = hasValidDate ? formatShortDate(sharedDate, i18n.language) : '';
                            const meta = [statusLine, dateText].filter(Boolean).join(' · ');
                            return (
                                <div key={index} className="rev-shared-row">
                                    <RevChip tone={SHARE_STATUS_TONE[item.status]}>{t(item.status)}</RevChip>
                                    <div className="rev-shared-row__main">
                                        <span className="rev-shared-row__email">{item.email}</span>
                                        {meta && <span className="rev-help">{meta}</span>}
                                    </div>
                                    <span className="rev-shared-row__actions">
                                        <RevButton
                                            size="sm"
                                            variant={item.status === 'removed' ? 'secondary' : 'danger'}
                                            disabled={!listaLista}
                                            onClick={() => handleStatusChange(item.email, item.status === 'removed' ? 'pending' : 'removed')}
                                        >
                                            {t(item.status === 'removed' ? 'Share again' : 'Stop sharing')}
                                        </RevButton>
                                    </span>
                                </div>
                            );
                        })}
                    </RevCard>
                </div>

                <div className="rev-cols__aside">
                    <RevCard title={t('eventSharedWith.permissions.title')}>
                        <RevKvRow ruled tone="brand" k={t('eventSharedWith.permissions.upload')} v={t('eventSharedWith.permissions.yes')} />
                        <RevKvRow ruled tone="brand" k={t('eventSharedWith.permissions.manageOwn')} v={t('eventSharedWith.permissions.yes')} />
                        <RevKvRow ruled tone="brand" k={t('eventSharedWith.permissions.summary')} v={t('eventSharedWith.permissions.yes')} />
                        <RevKvRow ruled tone="no" k={t('eventSharedWith.permissions.manageOthers')} v={t('eventSharedWith.permissions.no')} />
                        <RevKvRow ruled tone="no" k={t('eventSharedWith.permissions.pricing')} v={t('eventSharedWith.permissions.no')} />
                        <RevKvRow ruled tone="no" k={t('eventSharedWith.permissions.credits')} v={t('eventSharedWith.permissions.no')} />
                    </RevCard>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = state => ({
    event: state.eventsReducer.selectedEvent,
    loading: state.eventsReducer.loading,
});

const mapDispatchToProps = {
    getEventSharedWith,
    editEventSharedWith
};

export default connect(mapStateToProps, mapDispatchToProps)(EventSharedWith);
